import type {
  RecipientEncryptionResult,
  RecipientKey,
} from '@/types/poc/auth';

const UINT16_BYTES = 2;
const BYTE_MASK = 0xff;
const BYTE_SHIFT = 8;

export interface EncryptionResult {
  encryptedFile: File;
  encryptedFileKey: File;
  recipientResults: RecipientEncryptionResult[];
  originalFileName: string;
  originalFileSize: number;
  originalFileType: string;
}

export interface DecryptionResult {
  decryptedFile: File;
  originalFileName: string;
  originalFileSize: number;
  originalFileType: string;
}

export interface ExtractedComponents {
  data: Uint8Array;
  iv: Uint8Array;
  authTag: Uint8Array;
}

export abstract class FileEncryptionService {
  abstract encryptFile(
    file: File,
    ukek: Uint8Array,
    recipients: Array<{ id: string; publicKey: Uint8Array }>
  ): Promise<EncryptionResult>;
  abstract decryptFileWithEncryptedKey(
    encryptedFile: File,
    encryptedFileKey: File,
    ukek: Uint8Array
  ): Promise<DecryptionResult>;
  abstract encryptFileKeyForRecipients(
    fileKey: Uint8Array,
    recipients?: RecipientKey[]
  ): Promise<RecipientEncryptionResult[]>;
  abstract decryptFileKeyForRecipient(
    recipientResult: RecipientEncryptionResult,
    recipientPrivateKey: Uint8Array
  ): Promise<Uint8Array>;
  abstract reEncryptFileKeyForRecipient(
    facilitatorEncryption: RecipientEncryptionResult,
    facilitatorPrivateKey: Uint8Array,
    recipientPublicKey: Uint8Array
  ): Promise<{
    cipherText: Uint8Array;
    encryptedFileKey: {
      encryptedData: Uint8Array;
      iv: Uint8Array;
      authTag: Uint8Array;
    };
  }>;
  abstract decryptFileAsRecipient(
    encryptedFile: File,
    recipientResult: RecipientEncryptionResult,
    recipientPrivateKey: Uint8Array
  ): Promise<DecryptionResult>;

  static async readFileAsUint8Array(file: File): Promise<Uint8Array> {
    return new Uint8Array(await file.arrayBuffer());
  }

  static async blobToUint8Array(blob: Blob): Promise<Uint8Array> {
    return new Uint8Array(await blob.arrayBuffer());
  }

  static combineEncryptedComponents(
    encryptedData: Uint8Array,
    iv: Uint8Array,
    authTag: Uint8Array
  ): Blob {
    const totalSize =
      UINT16_BYTES + iv.length + UINT16_BYTES + authTag.length + encryptedData.length;
    const buffer = new ArrayBuffer(totalSize);
    const view = new Uint8Array(buffer);

    let offset = 0;
    view[offset++] = (iv.length >> BYTE_SHIFT) & BYTE_MASK;
    view[offset++] = iv.length & BYTE_MASK;
    view.set(iv, offset);
    offset += iv.length;

    view[offset++] = (authTag.length >> BYTE_SHIFT) & BYTE_MASK;
    view[offset++] = authTag.length & BYTE_MASK;
    view.set(authTag, offset);
    offset += authTag.length;

    view.set(encryptedData, offset);
    return new Blob([buffer]);
  }

  static extractEncryptedComponents(combined: Uint8Array): ExtractedComponents {
    const ivLength = (combined[0] << BYTE_SHIFT) | combined[1];
    const iv = combined.slice(UINT16_BYTES, UINT16_BYTES + ivLength);

    const authTagLengthOffset = UINT16_BYTES + ivLength;
    const authTagLength =
      (combined[authTagLengthOffset] << BYTE_SHIFT) |
      combined[authTagLengthOffset + 1];

    const authTagOffset = authTagLengthOffset + UINT16_BYTES;
    const authTag = combined.slice(authTagOffset, authTagOffset + authTagLength);
    const dataOffset = authTagOffset + authTagLength;
    const data = combined.slice(dataOffset);

    return { data, iv, authTag };
  }
}
